class ProductDisclosureText {
  constructor(element) {
    this.element = element;
    this.textEl = element.querySelector('.js-disclosure-text');
    this.toggleEl = element.querySelector('.js-disclosure-toggle');
    this.detailsEl = element.closest('details');

    if (!this.textEl || !this.toggleEl) return;

    this.MAX_LINES = 4;
    this.MIN_CHARS = 150;
    this.isInitialized = false;

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    // Function to setup when element is visible
    const setupWhenReady = () => {
      if (this.isInitialized) return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#checkAndSetup();
        });
      });
    };

    if (this.detailsEl) {
      // If details is already open, check immediately
      if (this.detailsEl.open) {
        setupWhenReady();
      } else {
        // Listen for when details opens
        const handleToggle = () => {
          if (this.detailsEl.open && !this.isInitialized) {
            setupWhenReady();
          }
        };

        this.detailsEl.addEventListener('toggle', handleToggle);

        // Also check immediately (details might be opened by JS before toggle event)
        // Use a small delay to allow any other scripts to run first
        setTimeout(setupWhenReady, 100);
      }
    } else {
      // No details element, check immediately
      setupWhenReady();
    }
  }

  #checkAndSetup() {
    if (this.isInitialized) return;

    // Temporarily remove constraints to measure actual height
    const computedStyle = getComputedStyle(this.textEl);
    const originalMaxHeight = this.textEl.style.maxHeight;
    const originalOverflow = this.textEl.style.overflow;
    const originalWebkitLineClamp = this.textEl.style.webkitLineClamp;
    const originalDisplay = this.textEl.style.display;

    // Make element visible and unconstrained to measure true height
    // If details is closed, we need to make the panel temporarily visible
    let panelWasHidden = false;
    let panelOriginalStyles = {};

    if (this.detailsEl && !this.detailsEl.open) {
      const panel = this.detailsEl.querySelector('.disclosure__panel');
      if (panel) {
        const panelStyle = getComputedStyle(panel);
        if (panelStyle.display === 'none' || panelStyle.height === '0px') {
          panelWasHidden = true;
          panelOriginalStyles.display = panel.style.display;
          panelOriginalStyles.height = panel.style.height;
          panelOriginalStyles.visibility = panel.style.visibility;
          panelOriginalStyles.position = panel.style.position;

          panel.style.display = 'block';
          panel.style.height = 'auto';
          panel.style.visibility = 'hidden';
          panel.style.position = 'absolute';
        }
      }
    }

    // Remove all text constraints
    this.textEl.style.maxHeight = 'none';
    this.textEl.style.overflow = 'visible';
    this.textEl.style.webkitLineClamp = 'none';
    this.textEl.style.display = 'block';

    // Get line height - try multiple methods
    let lineHeight = parseFloat(computedStyle.lineHeight);
    if (Number.isNaN(lineHeight) || lineHeight === 0 || lineHeight < 1) {
      const fontSize = parseFloat(computedStyle.fontSize);
      lineHeight = fontSize * 1.5;
    }

    const collapsedHeight = lineHeight * this.MAX_LINES;
    const contentHeight = this.textEl.scrollHeight;
    const contentLength = this.textEl.textContent.trim().length;

    // Restore original styles
    this.textEl.style.maxHeight = originalMaxHeight;
    this.textEl.style.overflow = originalOverflow;
    this.textEl.style.webkitLineClamp = originalWebkitLineClamp;
    this.textEl.style.display = originalDisplay;

    // Restore panel if we modified it
    if (panelWasHidden && this.detailsEl) {
      const panel = this.detailsEl.querySelector('.disclosure__panel');
      if (panel) {
        Object.keys(panelOriginalStyles).forEach((key) => {
          panel.style[key] = panelOriginalStyles[key];
        });
      }
    }

    const needsToggle = contentHeight > collapsedHeight + 2 && contentLength >= this.MIN_CHARS;

    if (!needsToggle) {
      this.toggleEl.remove();
      this.textEl.style.maxHeight = '';
      this.textEl.style.overflow = '';
      this.textEl.style.webkitLineClamp = '';
      this.isInitialized = true;
      return;
    }

    // Setup toggle functionality - use both maxHeight and webkit-line-clamp for better browser support
    const hasWebkitLineClamp = CSS.supports('-webkit-line-clamp', '1');

    if (hasWebkitLineClamp) {
      this.textEl.style.display = '-webkit-box';
      this.textEl.style.webkitBoxOrient = 'vertical';
      this.textEl.style.webkitLineClamp = `${this.MAX_LINES}`;
      this.textEl.style.overflow = 'hidden';
    } else {
      // Fallback for browsers that don't support webkit-line-clamp
      this.textEl.style.maxHeight = `${collapsedHeight}px`;
      this.textEl.style.overflow = 'hidden';
    }

    this.toggleEl.hidden = false;

    this.toggleEl.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#toggle();
    });

    this.isInitialized = true;
  }

  #toggle() {
    const isExpanded = this.textEl.classList.contains('is-expanded');
    const hasWebkitLineClamp = CSS.supports('-webkit-line-clamp', '1');

    if (isExpanded) {
      // Collapse
      const computedStyle = getComputedStyle(this.textEl);
      let lineHeight = parseFloat(computedStyle.lineHeight);
      if (Number.isNaN(lineHeight) || lineHeight === 0 || lineHeight < 1) {
        const fontSize = parseFloat(computedStyle.fontSize);
        lineHeight = fontSize * 1.5;
      }
      const collapsedHeight = lineHeight * this.MAX_LINES;

      if (hasWebkitLineClamp) {
        this.textEl.style.display = '-webkit-box';
        this.textEl.style.webkitBoxOrient = 'vertical';
        this.textEl.style.webkitLineClamp = `${this.MAX_LINES}`;
        this.textEl.style.overflow = 'hidden';
        this.textEl.style.maxHeight = '';
      } else {
        this.textEl.style.maxHeight = `${collapsedHeight}px`;
        this.textEl.style.overflow = 'hidden';
      }

      this.textEl.classList.remove('is-expanded');
      this.toggleEl.textContent = 'Show more +';
    } else {
      // Expand
      this.textEl.style.maxHeight = '';
      this.textEl.style.webkitLineClamp = '';
      this.textEl.style.display = '';
      this.textEl.style.overflow = '';
      this.textEl.style.webkitBoxOrient = '';
      this.textEl.classList.add('is-expanded');
      this.toggleEl.textContent = 'Show less -';
    }
  }
}

// Initialize all disclosures
function initProductDisclosureTexts() {
  document.querySelectorAll('.js-disclosure-content').forEach((el) => {
    if (!el.dataset.disclosureTextInit) {
      el.dataset.disclosureTextInit = 'true';
      new ProductDisclosureText(el);
    }
  });

  // Handle mobile disclosure description class
  const updateMobileClasses = () => {
    if (window.innerWidth <= 768) {
      document.querySelectorAll('.product-info__disclosure').forEach((el, i) => {
        if (i === 0) {
          el.classList.add('disclosure__description');
        } else {
          el.classList.remove('disclosure__description');
        }
      });
    } else {
      document.querySelectorAll('.product-info__disclosure').forEach((el) => {
        el.classList.remove('disclosure__description');
      });
    }
  };

  updateMobileClasses();

  let resizeTimeout;
  window.addEventListener(
    'resize',
    () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateMobileClasses, 150);
    },
    { passive: true },
  );
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductDisclosureTexts);
} else {
  initProductDisclosureTexts();
}

// Watch for dynamically added disclosures
const observer = new MutationObserver((mutations) => {
  let shouldCheck = false;

  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      if (
        node.matches?.('.js-disclosure-content') ||
        node.querySelector?.('.js-disclosure-content')
      ) {
        shouldCheck = true;
      }
    });
  });

  if (shouldCheck) {
    initProductDisclosureTexts();
  }
});

observer.observe(document.body, { childList: true, subtree: true });
